import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action } from "@ngrx/store";
import { catchError, concatMap, delay, filter,from, map, Observable, of, switchMap, takeUntil, takeWhile, tap, timer } from 'rxjs';

import { DownloadService } from '../../services/download.service';
import { ReportingService } from '../../services/reporting.service';
import { DownloadActions } from '../actions/download.actions';

@Injectable()
export class DownloadEffects {
  public constructor(
    private readonly actions$: Actions,
    private readonly service: DownloadService,
    private readonly reportingService: ReportingService
  ) {}

  public get: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DownloadActions.get),
      switchMap(({ organisationId, downloadId, admin }) =>
        from(this.service.get(organisationId, downloadId, admin)).pipe(
          map((download) => DownloadActions.getSuccess({ download: download })),
          catchError((error) => of(DownloadActions.getFailure({ error }))),
        ),
      ),
    ),
  );

  public generateOrderResultsReport: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DownloadActions.generateOrderResultsReport),
      switchMap(({ organisationId, orderId, admin }) =>
        from(this.reportingService.generateOrderResultsReport(organisationId, orderId, admin)).pipe(
          map((download) => DownloadActions.generateOrderResultsReportSuccess({
            download: download,
            organisationId,
            admin
          })),
          catchError((error) => of(DownloadActions.generateOrderResultsReportFailure({ error }))),
        ),
      ),
    ),
  );

  public startPollingAfterReportGeneration: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DownloadActions.generateOrderResultsReportSuccess),
      map(({ download, organisationId, admin }) =>
        DownloadActions.startPolling({
          organisationId,
          downloadId: download.id,
          admin,
          maxAttempts: 15
        })
      )
    ),
  );

  public polling: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DownloadActions.startPolling),
      switchMap(({ organisationId, downloadId, admin, maxAttempts = 15 }) => {
        let attemptCount = 0;

        return timer(1000, 1000).pipe(
          takeWhile(() => attemptCount < maxAttempts),
          takeUntil(this.actions$.pipe(ofType(DownloadActions.stopPolling))),
          takeUntil(
            this.actions$.pipe(
              ofType(DownloadActions.getSuccess),
              map(({ download }) => download),
              filter(download =>
                (download.url !== null && download.processed) || download.failed
              )
            )
          ),
          tap(() => attemptCount++),
          concatMap(() => [
            DownloadActions.get({ organisationId, downloadId, admin })
          ]),
          catchError((error) => {
            console.error('Polling failed:', error);
            return of(DownloadActions.getFailure({ error }));
          })
        ).pipe(
          switchMap((action) => {
            if (attemptCount >= maxAttempts) {
              return of(action, DownloadActions.pollingTimeout({ downloadId }));
            }
            return of(action);
          })
        );
      })
    ),
  );

  public stopPollingOnCompletion: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DownloadActions.getSuccess),
      map(({ download }) => download),
      filter(download => download.url !== null && download.processed),
      tap((download) => {
        this.triggerBrowserDownload(download.url);
      }),
      map(() => DownloadActions.stopPolling())
    ),
  );

  public handleFailedDownload: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DownloadActions.getSuccess),
      map(({ download }) => download),
      filter(download => download.failed),
      map((download) => {
        const errorMessage = download.failedReason || 'Report generation failed. Please try again.';
        const error = new HttpErrorResponse({
          error: { message: errorMessage },
          status: 422,
          statusText: 'Download Failed'
        });
        return DownloadActions.getFailure({ error });
      })
    ),
  );

  public pollingTimeout: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DownloadActions.pollingTimeout),
      delay(100),
      map(() => DownloadActions.stopPolling())
    ),
  );

  private triggerBrowserDownload(url: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = '';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
